import { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BookOpen, MapPin, ShoppingCart, ArrowLeft, Star, Truck, Zap, Calendar, AlertCircle, Edit2, Save, X, Upload, Image, Link } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, differenceInDays } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { isPlatformAdmin } from '../lib/roles';
import { checkRentEligibility } from '../lib/rentEligibility';
import { saveCheckoutResume } from '../lib/checkoutResume';
import RentCriteriaDialog from '../components/RentCriteriaDialog';
import { calculateRentalCharges } from '../lib/rentalPricing';

const BookDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [book, setBook] = useState(null);
  const [weeks, setWeeks] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState({
    also_viewed: [],
    top_rented: [],
    low_rented: []
  });
  
  // Date selection states — rental starts tomorrow (consistent with checkout)
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const defaultEnd = format(addDays(new Date(), 8), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(tomorrow);
  const [endDate, setEndDate] = useState(defaultEnd);
  
  // Edit mode states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [imageInputMode, setImageInputMode] = useState('url'); // 'url' or 'upload'
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBook();
    fetchRecommendations();
  }, [id]);

  const fetchBook = async () => {
    try {
      const userId = user?.id || null;
      const response = await axios.get(`${API}/books/${id}${userId ? `?user_id=${userId}` : ''}`);
      setBook(response.data);
    } catch (error) {
      toast.error('Failed to load book details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/books/${id}/recommendations`);
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to load recommendations', error);
    }
  };

  // Check if user can edit (book owner, Executive Leader, or Founder)
  const canEdit = user && (
    isPlatformAdmin(user.role) ||
    (user.role === 'admin' && book && book.owner_id === user.id)
  );

  const handleOpenEditDialog = () => {
    setEditForm({
      title: book.title || '',
      author: book.author || '',
      description: book.description || '',
      cover_image: book.cover_image || '',
      category: book.category || '',
      price: book.price || 0,
      stock: book.stock || 1,
      pincode: book.pincode || '',
      isbn_10: book.isbn_10 || '',
      isbn_13: book.isbn_13 || '',
      publisher: book.publisher || '',
      pages: book.pages || '',
      language: book.language || '',
      age_category: book.age_category || book.reading_age || ''
    });
    setImageInputMode(book.cover_image ? 'url' : 'url');
    setShowEditDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Only JPEG and PNG allowed
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Only JPEG and PNG formats are allowed');
      return;
    }

    // Validate file size (max 3MB)
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image too large. Please upload a cover under 3 MB.');
      return;
    }

    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditForm({ ...editForm, cover_image: reader.result });
        setUploadingImage(false);
        toast.success('Image uploaded successfully');
      };
      reader.onerror = () => {
        toast.error('Failed to read image file');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload image');
      setUploadingImage(false);
    }
  };

  const handleSaveBook = async () => {
    setSaving(true);
    try {
      // Calculate rental price as 30% of MRP
      // Clean up data - convert empty strings to null for optional fields
      const updatedData = {
        title: editForm.title,
        author: editForm.author,
        category: editForm.category || 'Others',
        price: parseFloat(editForm.price) || 0,
        stock: parseInt(editForm.stock) || 1,
        pincode: editForm.pincode || book.pincode,
        cover_image: editForm.cover_image || null,
        description: editForm.description || null,
        isbn_10: editForm.isbn_10 || null,
        isbn_13: editForm.isbn_13 || null,
        publisher: editForm.publisher || null,
        pages: editForm.pages ? parseInt(editForm.pages) : null,
        language: editForm.language || null,
        age_category: editForm.age_category || null,
        rental_price_per_week: Math.round(parseFloat(editForm.price) * 0.30)
      };

      await axios.put(
        `${API}/books/${book.id}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Book updated successfully!');
      setShowEditDialog(false);
      fetchBook(); // Refresh book data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update book');
    } finally {
      setSaving(false);
    }
  };

  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingCartAction, setPendingCartAction] = useState(null);
  const [categoryDiscount, setCategoryDiscount] = useState(0);
  const [rentEligibility, setRentEligibility] = useState(null);
  const [showRentCriteria, setShowRentCriteria] = useState(false);

  useEffect(() => {
    if (book) {
      axios.get(`${API}/categories`).then(res => {
        const cat = res.data.find(c => c.name === book.category);
        setCategoryDiscount(cat?.discount_percentage || 0);
      }).catch(() => {});
    }
  }, [book]);

  const ensureCanRent = async (qty = 1) => {
    const tokenNow = localStorage.getItem('token') || token;
    const result = await checkRentEligibility(
      [{ book_id: book.id, quantity: qty }],
      tokenNow
    );
    if (!result?.ok) {
      const startDateObj = startDate ? new Date(startDate) : null;
      const endDateObj = endDate ? new Date(endDate) : null;
      const days = startDateObj && endDateObj && !isNaN(startDateObj) && !isNaN(endDateObj)
        ? Math.max(1, differenceInDays(endDateObj, startDateObj))
        : 7;
      const weeks = Math.max(1, Math.ceil(days / 7));
      const original = book.rental_price_per_week;
      const discounted = categoryDiscount > 0
        ? Math.round(original * (1 - categoryDiscount / 100))
        : original;
      const shortfall = Math.max(
        0,
        Number(result?.blockers?.[0]?.shortfall)
          || (Number(result?.required_deposit || 0) - Number(result?.deposit_balance || 0))
      );
      saveCheckoutResume({
        cartItems: [{
          book_id: book.id,
          book,
          quantity: qty,
          weeks,
          discount_percentage: categoryDiscount,
          original_rental_price: original,
          discounted_rental_price: discounted,
        }],
        weeklyTotalAmount: discounted * qty,
        rentalStartDate: startDate,
        rentalEndDate: endDate,
        rentalDurationWeeks: weeks,
        rentalDays: days,
        directOrder: true,
        depositShortfall: shortfall,
      });
      try {
        await axios.post(
          `${API}/cart`,
          { book_id: book.id, quantity: qty, weeks, force: false },
          { headers: { Authorization: `Bearer ${tokenNow}` } }
        );
        window.dispatchEvent(new Event('cart-updated'));
      } catch {
        /* cart already has the book, or the request failed; checkout resume still has it */
      }
      setRentEligibility(result);
      setShowRentCriteria(true);
      return false;
    }
    return true;
  };

  const handleAddToCart = async (force = false) => {
    if (!user) {
      toast.error('Please login to add books to cart');
      navigate('/login');
      return;
    }
    if (!force) {
      try {
        const allowed = await ensureCanRent(quantity);
        if (!allowed) return;
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not verify rent criteria');
        return;
      }
    }

    try {
      const res = await axios.post(
        `${API}/cart`,
        { book_id: book.id, weeks: rentalWeeks, force },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.is_duplicate && !force) {
        setPendingCartAction('add');
        setShowDuplicateDialog(true);
        return;
      }
      toast.success('Book added to cart!');
      setShowDuplicateDialog(false);
      window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const handleConfirmDuplicate = async () => {
    if (pendingCartAction === 'add') {
      await handleAddToCart(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'var(--background)'}}>
        <div className="text-xl font-semibold text-gray-700">Loading...</div>
      </div>
    );
  }

  if (!book) return null;

  // Calculate rental duration based on dates
  const startDateObj = startDate ? new Date(startDate) : null;
  const endDateObj = endDate ? new Date(endDate) : null;
  const rentalDays = startDateObj && endDateObj && !isNaN(startDateObj) && !isNaN(endDateObj)
    ? Math.max(1, differenceInDays(endDateObj, startDateObj))
    : 7;
  const rentalWeeks = Math.max(1, Math.ceil(rentalDays / 7));
  const originalPricePerWeek = book.rental_price_per_week;
  const discountedPricePerWeek = categoryDiscount > 0 ? Math.round(originalPricePerWeek * (1 - categoryDiscount / 100)) : originalPricePerWeek;
  // MBR-114: charge by days (weekly rate / 7 × days), not ceil(weeks)
  const dailyRate = discountedPricePerWeek / 7;
  const rentalSubtotal = Math.round(dailyRate * rentalDays * quantity);
  const { platformFee, gst } = calculateRentalCharges(rentalSubtotal);
  const totalPrice = rentalSubtotal + platformFee + gst;

  const handlePlaceOrder = async () => {
    if (!user) {
      toast.error('Please login to place an order');
      navigate('/login');
      return;
    }
    try {
      const allowed = await ensureCanRent(quantity);
      if (!allowed) return;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not verify rent criteria');
      return;
    }

    navigate('/checkout', {
      state: {
        cartItems: [{
          book_id: book.id,
          book,
          quantity,
          weeks: rentalWeeks,
          discount_percentage: categoryDiscount,
          original_rental_price: originalPricePerWeek,
          discounted_rental_price: discountedPricePerWeek,
        }],
        weeklyTotalAmount: discountedPricePerWeek * quantity,
        rentalStartDate: startDate,
        rentalEndDate: endDate,
        rentalDurationWeeks: rentalWeeks,
        rentalDays,
        directOrder: true,
      },
    });
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8">
          {/* Edit Button for Admin/Super Admin */}
          {canEdit && (
            <div className="flex justify-end p-4 border-b">
              <Button
                onClick={handleOpenEditDialog}
                className="flex items-center space-x-2"
                style={{backgroundColor: 'var(--accent)', color: 'white'}}
                data-testid="edit-book-button"
              >
                <Edit2 className="h-4 w-4" />
                <span>Edit Book</span>
              </Button>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
            <div 
              className="rounded-2xl overflow-hidden relative" 
              style={{
                backgroundColor: '#f3ece0',
                minHeight: '420px',
                height: '100%'
              }}
            >
              {book.cover_image ? (
                <img
                  src={book.cover_image}
                  alt={book.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '420px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  data-testid="book-cover-image"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-center" style={{minHeight: '420px'}}>
                  <BookOpen className="h-32 w-32 mb-4" style={{color: 'var(--primary)'}} />
                  <p className="text-sm text-gray-400">No cover image available</p>
                </div>
              )}
            </div>

            <div className="flex flex-col justify-between">
              <div>
                <div className="mb-4">
                  {book.is_available ? (
                    <span className="inline-block text-green-800 text-sm font-semibold px-4 py-2 rounded-full" style={{backgroundColor: 'var(--card)'}} data-testid="book-status">
                      {book.stock} Available for Rent
                    </span>
                  ) : (
                    <span className="inline-block bg-red-100 text-red-800 text-sm font-semibold px-4 py-2 rounded-full" data-testid="book-status">
                      Currently Rented
                    </span>
                  )}
                </div>

                <h1 className="text-4xl font-bold mb-2" style={{color: 'var(--foreground)'}} data-testid="book-title">{book.title}</h1>
                <p className="text-xl text-gray-600 mb-4" data-testid="book-author">by {book.author}</p>

                <div className="flex items-center space-x-4 mb-6">
                  <div className="flex items-center space-x-1">
                    <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    <span className="font-semibold text-gray-700">{book.rating}</span>
                    <span className="text-gray-500">({book.customer_reviews_count} reviews)</span>
                  </div>
                  <div className="flex items-start text-gray-600">
                    <MapPin className="h-5 w-5 mr-1 mt-0.5 flex-shrink-0" />
                    <div>
                    <div className="flex flex-wrap gap-1.5" data-testid="book-pincodes">
                      {(book.available_locations?.length
                        ? book.available_locations
                        : (book.pincode ? [{ pincode: book.pincode, city: book.city, count: book.stock || 1 }] : [])
                      ).map((loc) => (
                        <span
                          key={loc.pincode}
                          className="inline-flex items-center text-sm bg-gray-100 text-gray-800 px-2.5 py-0.5 rounded-full"
                          title={`${loc.count || 1} cop${(loc.count || 1) === 1 ? 'y' : 'ies'}`}
                        >
                          {loc.city ? `${loc.city} · ` : ''}{loc.pincode}
                          {(loc.count || 1) > 1 ? ` (${loc.count})` : ''}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      If several copies are available, your order is sent to the owner at the nearest pincode.
                    </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl p-6 mb-6" style={{backgroundColor: 'var(--card)'}}>
                  <div className="flex items-baseline space-x-3 mb-2">
                    {categoryDiscount > 0 ? (
                      <>
                        <span className="text-4xl font-bold" style={{color: 'var(--accent)'}} data-testid="rental-price">₹{discountedPricePerWeek}</span>
                        <span className="text-lg text-gray-600">/week</span>
                        <span className="text-xl text-gray-400 line-through">₹{Math.round(originalPricePerWeek)}</span>
                        <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">{categoryDiscount}% OFF</span>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold" style={{color: 'var(--accent)'}} data-testid="rental-price">₹{Math.round(book.rental_price_per_week)}</span>
                        <span className="text-lg text-gray-600">/week</span>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">MRP: ₹{Math.round(book.price)}</p>
                  <p className="text-xs mt-2" style={{color: 'var(--accent)'}} data-testid="save-percent-note">Save 70% by renting!</p>
                </div>

                {/* Delivery details hidden */}

                {book.description && (
                  <div className="mb-6">
                    <h3 className="font-bold text-lg mb-2">Description</h3>
                    <p className="text-gray-700 leading-relaxed" data-testid="book-description">{book.description}</p>
                  </div>
                )}
              </div>

              {book.is_available && (
                <div className="border-t pt-6">
                  {/* Rental Date Selection */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <Calendar className="h-5 w-5 mr-2" style={{color: 'var(--primary)'}} />
                      Select Rental Period
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Date
                        </label>
                        <Input
                          type="date"
                          value={startDate}
                          min={tomorrow}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            const maxEnd = format(addDays(new Date(val), 360), 'yyyy-MM-dd');
                            if (new Date(endDate) > new Date(maxEnd)) {
                              setEndDate(maxEnd);
                              toast.error('Rental period cannot exceed 360 days');
                            } else if (new Date(val) >= new Date(endDate)) {
                              setEndDate(format(addDays(new Date(val), 7), 'yyyy-MM-dd'));
                            }
                            setStartDate(val);
                          }}
                          className="w-full h-12"
                          data-testid="start-date-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Date
                        </label>
                        <Input
                          type="date"
                          value={endDate}
                          min={format(addDays(new Date(startDate), 1), 'yyyy-MM-dd')}
                          max={format(addDays(new Date(startDate), 360), 'yyyy-MM-dd')}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            const days = differenceInDays(new Date(val), new Date(startDate));
                            if (days > 360) {
                              toast.error('Rental period cannot exceed 360 days');
                              setEndDate(format(addDays(new Date(startDate), 360), 'yyyy-MM-dd'));
                              return;
                            }
                            setEndDate(val);
                          }}
                          className="w-full h-12"
                          data-testid="end-date-input"
                        />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      Duration: <span className="font-semibold">{rentalDays} days ({rentalWeeks} {rentalWeeks === 1 ? 'week' : 'weeks'})</span>
                    </p>
                  </div>
                  
                  {/* Quantity Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max={book.stock}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.min(parseInt(e.target.value) || 1, book.stock))}
                      className="w-24 h-12 text-center text-lg font-semibold"
                      data-testid="quantity-input"
                    />
                    <span className="text-sm text-gray-500 ml-2">({book.stock} available)</span>
                  </div>
                  
                  {/* Price Breakdown */}
                  <div className="mb-4 p-4 rounded-lg" style={{backgroundColor: 'var(--card)'}}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Rental amount</p>
                        <p className="text-xs text-gray-500">
                          ₹{Math.round(dailyRate)}/day × {rentalDays} day{rentalDays !== 1 ? 's' : ''}
                          {quantity > 1 ? ` × ${quantity}` : ''}
                          {categoryDiscount > 0 && <span className="text-red-500"> ({categoryDiscount}% off)</span>}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">₹{rentalSubtotal}</p>
                    </div>
                    {categoryDiscount > 0 && (
                      <div className="flex justify-between text-xs text-green-600 mb-2">
                        <span>Category Discount ({categoryDiscount}%)</span>
                        <span>-₹{Math.round(((originalPricePerWeek - discountedPricePerWeek) / 7) * rentalDays * quantity)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-gray-700 mb-1">
                      <span>Platform fee</span>
                      <span>₹{platformFee}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-700 mb-2">
                      <span>GST</span>
                      <span>₹{gst}</span>
                    </div>
                    <div className="flex justify-between items-center border-t pt-2">
                      <p className="text-sm font-medium text-gray-900">Total Amount</p>
                      <p className="text-3xl font-bold" style={{color: 'var(--accent)'}} data-testid="total-price">₹{totalPrice}</p>
                    </div>
                  </div>
                  
                  {/* Extra Days Warning */}
                  <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-800 font-medium">Important Note</p>
                      <p className="text-xs text-amber-700">Extra days beyond the end date ({format(new Date(endDate), 'dd MMM yyyy')}) will be charged at ₹{Math.round(book.rental_price_per_week / 7)}/day as late fee.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Button
                      onClick={() => handleAddToCart()}
                      variant="outline"
                      className="h-12 flex items-center justify-center space-x-2"
                      data-testid="add-to-cart-button"
                    >
                      <ShoppingCart className="h-5 w-5" />
                      <span>Add to Cart</span>
                    </Button>
                    <Button
                      onClick={() => handlePlaceOrder()}
                      className="h-12 hover:opacity-90"
                      style={{backgroundColor: 'var(--primary)', color: 'white'}}
                      data-testid="place-order-button"
                    >
                      Place Order
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {recommendations.also_viewed.length > 0 && (
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6" style={{color: 'var(--foreground)'}}>Reads who viewed this book also viewed</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {recommendations.also_viewed.map((recBook) => (
                <div
                  key={recBook.id}
                  onClick={() => navigate(`/books/${recBook.id}`)}
                  className="cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className="h-48 flex items-center justify-center" style={{backgroundColor: 'var(--card)'}}>
                    {recBook.cover_image ? (
                      <img src={recBook.cover_image} alt={recBook.title} className="h-full w-full object-cover" />
                    ) : (
                      <BookOpen className="h-16 w-16" style={{color: 'var(--primary)'}} />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-semibold text-sm line-clamp-2">{recBook.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{recBook.author}</p>
                    <p className="text-sm font-bold mt-2" style={{color: 'var(--accent)'}}>₹{Math.round(recBook.rental_price_per_week)}/week</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold mb-6" style={{color: 'var(--foreground)'}}>Similar Books from {book.category}</h2>
          
          {recommendations.top_rented.length > 0 && (
            <>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Top Rented</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {recommendations.top_rented.map((simBook) => (
                  <div
                    key={simBook.id}
                    onClick={() => navigate(`/books/${simBook.id}`)}
                    className="cursor-pointer flex space-x-4 rounded-xl p-4 hover:shadow-lg transition-shadow"
                    style={{backgroundColor: 'var(--card)'}}
                  >
                    <div className="w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden" style={{backgroundColor: 'var(--secondary)'}}>
                      {simBook.cover_image ? (
                        <img src={simBook.cover_image} alt={simBook.title} className="h-full w-full object-cover" />
                      ) : (
                        <BookOpen className="h-full w-full p-4" style={{color: 'var(--primary)'}} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-lg line-clamp-2">{simBook.title}</p>
                      <p className="text-sm text-gray-600 mb-2">{simBook.author}</p>
                      <p className="text-lg font-bold" style={{color: 'var(--accent)'}}>₹{Math.round(simBook.rental_price_per_week)}/week</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {recommendations.low_rented.length > 0 && (
            <>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">More Options</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {recommendations.low_rented.map((simBook) => (
                  <div
                    key={simBook.id}
                    onClick={() => navigate(`/books/${simBook.id}`)}
                    className="cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow"
                  >
                    <div className="h-40 flex items-center justify-center" style={{backgroundColor: 'var(--card)'}}>
                      {simBook.cover_image ? (
                        <img src={simBook.cover_image} alt={simBook.title} className="h-full w-full object-cover" />
                      ) : (
                        <BookOpen className="h-12 w-12" style={{color: 'var(--primary)'}} />
                      )}
                    </div>
                    <div className="p-3">
                      <p className="font-semibold text-sm line-clamp-2">{simBook.title}</p>
                      <p className="text-xs text-gray-600 mt-1">{simBook.author}</p>
                      <p className="text-sm font-bold mt-2" style={{color: 'var(--accent)'}}>₹{Math.round(simBook.rental_price_per_week)}/week</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h2 className="text-2xl font-bold mb-6" style={{color: 'var(--foreground)'}}>Product Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">Customer Rating</span>
              <span className="font-semibold flex items-center">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 mr-1" />
                {book.rating} ({book.customer_reviews_count} reviews)
              </span>
            </div>
            {book.pages && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Pages</span>
                <span className="font-semibold">{book.pages}</span>
              </div>
            )}
            {(book.isbn_13 || book.isbn_10 || book.isbn) && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">ISBN</span>
                <span className="font-semibold">{book.isbn_13 || book.isbn_10 || book.isbn}</span>
              </div>
            )}
            {book.weight_gms && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Weight</span>
                <span className="font-semibold">{book.weight_gms} gms</span>
              </div>
            )}
            {book.size_cms && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Size</span>
                <span className="font-semibold">{book.size_cms} cms</span>
              </div>
            )}
            {book.publisher && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Publisher</span>
                <span className="font-semibold">{book.publisher}</span>
              </div>
            )}
            {book.language && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Language</span>
                <span className="font-semibold">{book.language}</span>
              </div>
            )}
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">Format</span>
              <span className="font-semibold">{book.paperback ? 'Paperback' : 'Hardcover'}</span>
            </div>
            {(book.age_category || book.reading_age) && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Age Category</span>
                <span className="font-semibold">{book.age_category || book.reading_age}</span>
              </div>
            )}
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-600">MRP</span>
              <span className="font-semibold">₹{book.price}</span>
            </div>
            {book.country_of_origin && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Country of Origin</span>
                <span className="font-semibold">{book.country_of_origin}</span>
              </div>
            )}
            {book.packer && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Packer</span>
                <span className="font-semibold">{book.packer}</span>
              </div>
            )}
            {book.best_sellers_rank && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-600">Best Sellers Rank</span>
                <span className="font-semibold">{book.best_sellers_rank}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Book Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent 
          className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
          style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb'
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--foreground)', fontSize: '1.25rem', fontWeight: '700' }}>
              Edit Book Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="title" className="text-gray-700 font-medium">Title *</Label>
                <Input
                  id="title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-title"
                />
              </div>
              
              <div>
                <Label htmlFor="author" className="text-gray-700 font-medium">Author *</Label>
                <Input
                  id="author"
                  value={editForm.author}
                  onChange={(e) => setEditForm({...editForm, author: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-author"
                />
              </div>
              
              <div>
                <Label htmlFor="category" className="text-gray-700 font-medium">Category</Label>
                <Input
                  id="category"
                  value={editForm.category}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-category"
                />
              </div>
              
              <div>
                <Label htmlFor="price" className="text-gray-700 font-medium">MRP (₹) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={editForm.price}
                  onChange={(e) => setEditForm({...editForm, price: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-price"
                />
                <p className="text-xs text-gray-500 mt-1">Rental price: ₹{Math.round(parseFloat(editForm.price || 0) * 0.30)}/week</p>
              </div>
              
              <div>
                <Label htmlFor="stock" className="text-gray-700 font-medium">Stock</Label>
                <Input
                  id="stock"
                  type="number"
                  value={editForm.stock}
                  onChange={(e) => setEditForm({...editForm, stock: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-stock"
                />
              </div>
              
              {/* Age Category */}
              <div>
                <Label htmlFor="age_category" className="text-gray-700 font-medium">Age Category</Label>
                <select
                  id="age_category"
                  value={editForm.age_category}
                  onChange={(e) => setEditForm({...editForm, age_category: e.target.value})}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  data-testid="edit-age-category"
                >
                  <option value="">Select Age</option>
                  <option value="4-12">4-12</option>
                  <option value="13-21">13-21</option>
                  <option value="22-30">22-30</option>
                  <option value="30+">30+</option>
                </select>
              </div>

              {/* Image Upload Section */}
              <div className="col-span-2 p-4 rounded-lg border border-gray-200 bg-gray-50">
                <Label className="text-gray-700 font-medium mb-3 block">Book Cover Image</Label>
                
                {/* Toggle between URL and Upload */}
                <div className="flex space-x-2 mb-3">
                  <Button
                    type="button"
                    size="sm"
                    variant={imageInputMode === 'url' ? 'default' : 'outline'}
                    onClick={() => setImageInputMode('url')}
                    className={imageInputMode === 'url' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-gray-700'}
                  >
                    <Link className="h-4 w-4 mr-1" />
                    URL
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={imageInputMode === 'upload' ? 'default' : 'outline'}
                    onClick={() => setImageInputMode('upload')}
                    className={imageInputMode === 'upload' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'text-gray-700'}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Upload
                  </Button>
                </div>

                {imageInputMode === 'url' ? (
                  <Input
                    id="cover_image"
                    value={editForm.cover_image}
                    onChange={(e) => setEditForm({...editForm, cover_image: e.target.value})}
                    placeholder="https://example.com/book-cover.jpg"
                    className="border-gray-300 focus:border-blue-500"
                    data-testid="edit-cover-image-url"
                  />
                ) : (
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/jpg,image/png"
                      className="hidden"
                      data-testid="edit-cover-image-file"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="w-full h-20 border-dashed border-2 border-gray-300 hover:border-blue-500 bg-white text-gray-600"
                    >
                      {uploadingImage ? (
                        <span className="flex items-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                          Uploading...
                        </span>
                      ) : (
                        <span className="flex flex-col items-center">
                          <Image className="h-6 w-6 mb-1 text-gray-500" />
                          <span className="text-sm">Click to upload image</span>
                          <span className="text-xs text-gray-400">Max 3 MB. JPEG/PNG only. Recommended: 600x900 px</span>
                        </span>
                      )}
                    </Button>
                  </div>
                )}

                {/* Image Preview */}
                {editForm.cover_image && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200 flex items-center space-x-3">
                    <img 
                      src={editForm.cover_image} 
                      alt="Preview" 
                      className="h-20 w-16 object-cover rounded shadow-sm"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">Image Preview</p>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditForm({...editForm, cover_image: ''})}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 h-7 mt-1"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="description" className="text-gray-700 font-medium">Description</Label>
                <Textarea
                  id="description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={3}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-description"
                />
              </div>
              
              <div>
                <Label htmlFor="pincode" className="text-gray-700 font-medium">Pincode</Label>
                <Input
                  id="pincode"
                  value={editForm.pincode}
                  onChange={(e) => setEditForm({...editForm, pincode: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-pincode"
                />
              </div>
              
              <div>
                <Label htmlFor="language" className="text-gray-700 font-medium">Language</Label>
                <Input
                  id="language"
                  value={editForm.language}
                  onChange={(e) => setEditForm({...editForm, language: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-language"
                />
              </div>
              
              <div>
                <Label htmlFor="isbn_10" className="text-gray-700 font-medium">ISBN-10</Label>
                <Input
                  id="isbn_10"
                  value={editForm.isbn_10}
                  onChange={(e) => setEditForm({...editForm, isbn_10: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-isbn10"
                />
              </div>
              
              <div>
                <Label htmlFor="isbn_13" className="text-gray-700 font-medium">ISBN-13</Label>
                <Input
                  id="isbn_13"
                  value={editForm.isbn_13}
                  onChange={(e) => setEditForm({...editForm, isbn_13: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-isbn13"
                />
              </div>
              
              <div>
                <Label htmlFor="publisher" className="text-gray-700 font-medium">Publisher</Label>
                <Input
                  id="publisher"
                  value={editForm.publisher}
                  onChange={(e) => setEditForm({...editForm, publisher: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-publisher"
                />
              </div>
              
              <div>
                <Label htmlFor="pages" className="text-gray-700 font-medium">Pages</Label>
                <Input
                  id="pages"
                  value={editForm.pages}
                  onChange={(e) => setEditForm({...editForm, pages: e.target.value})}
                  className="mt-1 border-gray-300 focus:border-blue-500"
                  data-testid="edit-pages"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
              <Button 
                variant="outline" 
                onClick={() => setShowEditDialog(false)}
                className="text-gray-700 border-gray-300 hover:bg-gray-100"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSaveBook} 
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="save-book-button"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />

      <RentCriteriaDialog
        open={showRentCriteria}
        onOpenChange={setShowRentCriteria}
        eligibility={rentEligibility}
      />

      {/* Duplicate Book Confirmation Dialog — MBR-111 */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-md border-2 border-red-500 bg-red-50">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-700">Duplicate Book in Cart</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-red-900 mt-2 font-medium">
            You are ordering more than one copy of <strong>{book?.title}</strong>. Do you want to continue?
          </p>
          <p className="text-xs text-red-700 mt-1">The quantity will be increased instead of creating a duplicate entry.</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1 border-red-300" onClick={() => setShowDuplicateDialog(false)} data-testid="cancel-duplicate-btn">
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirmDuplicate}
              data-testid="confirm-duplicate-btn"
            >
              Yes, Add Another
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookDetailsPage;